'use client';

import { ComponentProps, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import FormattedMessage from '@alltrails/layout/components/FormattedMessage';
import FaqSection from '@alltrails/layout/components/FrequentlyAskedQuestion/FaqSection';
import ShowMoreFaqs from '@alltrails/layout/components/ShowMoreFaqs';
import useLogWebSectionViewed from '@alltrails/layout/hooks/useLogWebSectionViewed';
import { getAnalyticsPageType } from '@/utils/getAnalyticsPageType';
import { FaqDataType, LocationDetailsResponseType, PageFilterType } from '../../types/locationDetailsPages/shared';
import { useAppSelector } from '../../utils/redux';
import PageSection from '../layouts/PageSection';
import SectionHeader from '../SectionHeader';
import { BestTrailAnswer, getBestTrailQuestion } from './BestTrailFaq';
import { BikingAnswer, getBikingQuestion } from './BikingFaq';
import { CampingAnswer, getCampingQuestion } from './CampingFaq';
import { getElevationQuestion, LocationElevationAnswer, ParkElevationAnswer } from './ElevationFaq';
import styles from './LocationFAQ.module.scss';
import { getLongestTrailQuestion, LongestTrailAnswer } from './LongestTrailFaq';
import { getNationalParkQuestion, NationalParkAnswer, NationalParksFaqPageFilter } from './NationalParksFaq/NationalParksFaq';
import { getParkAggregatesQuestion, ParkAggregatesAnswer } from './ParkAggregatesFaq';
import { getPoiTypeQuestion, type PoiFaqType, PoiTypeAnswer } from './PoiTypeFaq/PoiTypeFaq';
import { getPopularDifficultTrailQuestion, PopularDifficultTrailAnswer } from './PopularDifficultFaq';
import { getRunningQuestion, RunningAnswer } from './RunningFaq';
import { getSeasonQuestion, SeasonAnswer, type SeasonNameType } from './SeasonalFaq/SeasonalFaq';
import { FaqFilterType } from './types';
import { getTypesOfTrailsQuestion, TypesOfTrailsAnswer } from './TypesOfTrailsFaq';

type Props = {
  type: LocationDetailsResponseType;
  name: string;
  faqData: FaqDataType;
  pageFilterUid?: PageFilterType;
  isBot?: boolean;
  id: number;
};

const FAQ_ALLOWED_FOR_FILTERS = [
  'backpacking',
  'bike-touring',
  'birding',
  'camping',
  'fishing',
  'hiking',
  'horseback-riding',
  'mountain-biking',
  'off-road-driving',
  'road-biking',
  'rock-climbing',
  'scenic-driving',
  'skiing',
  'snowshoeing',
  'trail-running',
  'via-ferrata',
  'walking',
  'cross-country-skiing',
  'ada',
  'beach',
  'cave',
  'city-walk',
  'dogs',
  'forest',
  'historic-site',
  'hot-springs',
  'kids',
  'lake',
  'partially-paved',
  'paved',
  'pub-crawl',
  'rails-trails',
  'river',
  'strollers',
  'waterfall',
  'wildlife'
];

const LocationFAQ = ({ type, name, faqData, pageFilterUid, isBot, id }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const pageType = getAnalyticsPageType(type);
  const languageRegionCode = useLanguageRegionCode();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const isLocationPage = type === 'city' || type === 'state' || type === 'country';
  useLogWebSectionViewed(ref, 'FAQ', pageType, { threshold: 0.4 });

  if (pageFilterUid && !FAQ_ALLOWED_FOR_FILTERS.includes(pageFilterUid)) {
    return null;
  }

  const {
    longestTrail,
    bestTrail,
    popularDifficultTrail,
    mostElevationGainTrails,
    faqTrailTypes,
    faqNationalParks,
    faqParkAggregates,
    campingTrails,
    runningTrails,
    bikingTrails,
    northernSeasonalTrails,
    poiTypes
  } = faqData;

  const pageFilter = pageFilterUid as FaqFilterType;

  const entries: ComponentProps<typeof ShowMoreFaqs>['faqEntries'] = [];

  // "what national parks have hiking trails" is only shown on region and country pages
  if (isLocationPage && faqNationalParks && faqNationalParks.length > 0 && !['pub-crawl', 'city-walk'].includes(pageFilter)) {
    const nationalParksPageFilter = pageFilter as NationalParksFaqPageFilter;
    entries.push({
      question: getNationalParkQuestion(intl, name, nationalParksPageFilter),
      answer: (
        <NationalParkAnswer
          filter={nationalParksPageFilter}
          locationName={name}
          faqNationalParks={faqNationalParks}
          intl={intl}
          languageRegionCode={languageRegionCode}
        />
      ),
      key: 'faq-national-parks'
    });
  }

  // "different types of hiking trails" is only shown on city, region, country and POI pages
  if (['city', 'state', 'country', 'poi'].includes(type) && !pageFilter && faqTrailTypes && faqTrailTypes.length > 0) {
    entries.push({
      question: getTypesOfTrailsQuestion(intl, name, type),
      answer: (
        <TypesOfTrailsAnswer faqTrailTypes={faqTrailTypes} placeName={name} locationType={type} intl={intl} languageRegionCode={languageRegionCode} />
      ),
      key: 'faq-hiking-trail-types'
    });
  }

  // "best place to hike" is shown on park and POI pages
  if (bestTrail && ['park', 'poi'].includes(type)) {
    entries.push({
      question: getBestTrailQuestion(intl, name, type, pageFilter),
      answer: (
        <BestTrailAnswer
          filter={pageFilter}
          placeName={name}
          bestTrail={bestTrail}
          locationType={type}
          languageRegionCode={languageRegionCode}
          intl={intl}
          displayMetric={displayMetric}
        />
      ),
      key: 'faq-hiking-best-place'
    });
  }

  if (longestTrail) {
    entries.push({
      question: getLongestTrailQuestion(intl, name, type, pageFilter),
      answer: (
        <LongestTrailAnswer
          filter={pageFilter}
          locationName={name}
          locationType={type}
          longestTrail={longestTrail}
          languageRegionCode={languageRegionCode}
          intl={intl}
          displayMetric={displayMetric}
        />
      ),
      key: 'faq-longest-trail'
    });
  }

  const parkHasTrailsAndReviews = faqParkAggregates && faqParkAggregates.totalTrails > 0 && faqParkAggregates.reviewCount > 0;
  if (type === 'park' && parkHasTrailsAndReviews) {
    entries.push({
      question: getParkAggregatesQuestion(intl, name, pageFilter),
      answer: (
        <ParkAggregatesAnswer
          filter={pageFilter}
          locationName={name}
          faqParkAggregates={faqParkAggregates}
          intl={intl}
          languageRegionCode={languageRegionCode}
        />
      ),
      key: 'faq-park-aggregates'
    });
  }

  if (type === 'park' && mostElevationGainTrails) {
    entries.push({
      question: getElevationQuestion(intl, name, type, pageFilter),
      answer: (
        <ParkElevationAnswer
          filter={pageFilter}
          locationName={name}
          mostElevationGainTrails={mostElevationGainTrails}
          languageRegionCode={languageRegionCode}
          intl={intl}
          displayMetric={displayMetric}
        />
      ),
      key: 'faq-park-elevations'
    });
  }

  if (['city', 'state', 'country', 'poi'].includes(type) && mostElevationGainTrails) {
    entries.push({
      question: getElevationQuestion(intl, name, type, pageFilter),
      answer: (
        <LocationElevationAnswer
          filter={pageFilter}
          locationName={name}
          mostElevationGainTrails={mostElevationGainTrails}
          languageRegionCode={languageRegionCode}
          intl={intl}
          displayMetric={displayMetric}
        />
      ),
      key: 'faq-location-elevations'
    });
  }

  if (['city', 'state', 'country', 'poi'].includes(type) && popularDifficultTrail) {
    entries.push({
      question: getPopularDifficultTrailQuestion(intl, name, type, pageFilter),
      answer: (
        <PopularDifficultTrailAnswer
          filter={pageFilter}
          locationName={name}
          popularDifficultTrail={popularDifficultTrail}
          locationType={type}
          intl={intl}
          languageRegionCode={languageRegionCode}
        />
      ),
      key: 'faq-popular-difficult-trail'
    });
  }

  if (campingTrails && campingTrails.count > 0) {
    entries.push({
      question: getCampingQuestion(intl, name),
      answer: <CampingAnswer locationName={name} campingTrails={campingTrails} languageRegionCode={languageRegionCode} intl={intl} />,
      key: 'faq-camping'
    });
  }

  if (runningTrails && runningTrails.count > 0) {
    entries.push({
      question: getRunningQuestion(intl, name),
      answer: <RunningAnswer locationName={name} runningTrails={runningTrails} intl={intl} languageRegionCode={languageRegionCode} />,
      key: 'faq-running'
    });
  }

  if (bikingTrails && bikingTrails.count > 0) {
    entries.push({
      question: getBikingQuestion(intl, name),
      answer: <BikingAnswer locationName={name} bikingTrails={bikingTrails} languageRegionCode={languageRegionCode} intl={intl} />,
      key: 'faq-biking'
    });
  }

  // This will only return data for northernSeasonalTrails for locations in US and Canada
  if (northernSeasonalTrails) {
    Object.keys(northernSeasonalTrails).forEach(season => {
      const seasonTrails = northernSeasonalTrails[season];
      if (seasonTrails.length) {
        entries.push({
          question: getSeasonQuestion(name, season as SeasonNameType, intl),
          answer: (
            <SeasonAnswer
              locationName={name}
              seasonName={season as SeasonNameType}
              trails={seasonTrails}
              languageRegionCode={languageRegionCode}
              intl={intl}
            />
          ),
          key: 'faq-seasonal'
        });
      }
    });
  }

  if (poiTypes && Object.keys(poiTypes).length > 0) {
    Object.entries(poiTypes).forEach(([poiType, { trailsCount, trails }]) => {
      if (trailsCount > 0) {
        entries.push({
          question: getPoiTypeQuestion(name, poiType as PoiFaqType, intl),
          answer: (
            <PoiTypeAnswer
              locationName={name}
              poiType={poiType as PoiFaqType}
              trailsCount={trailsCount}
              trails={trails}
              languageRegionCode={languageRegionCode}
              intl={intl}
            />
          ),
          key: `faq-poi-${poiType}`
        });
      }
    });
  }

  if (entries.length === 0) {
    return null;
  }

  return (
    <PageSection>
      <FaqSection className={styles.locationFAQSection} ref={ref}>
        <div className={styles.titleSection}>
          <SectionHeader title={<FormattedMessage defaultMessage="Frequently asked questions" />} className={styles.title} />
        </div>
        <div className={styles.answers}>
          <ShowMoreFaqs faqEntries={entries} id={id} isBot={isBot} pageType={pageType} />
        </div>
      </FaqSection>
    </PageSection>
  );
};

export default LocationFAQ;
